/**
 * 작성자 : 한영광
 * 작성일자 : 2024.09.04
 * 기능 : 오디오 파일 연결
 *
 * 수정 : crunker 는 항상 무압축 WAV 로만 export 되므로(원본 mp3 보다 파일이 커지는 원인),
 *        연결 후 lamejs 로 mp3 인코딩하여 다운로드하도록 변경.
 */

import Crunker, { CrunkerInputTypes } from 'crunker';
import * as lamejs from '@breezystack/lamejs';

// 출력 설정 (음성 방송용 기본값) - 필요 시 이 값만 조정하면 됩니다.
const OUTPUT_SAMPLE_RATE = 16000; // 출력 샘플레이트 (8000=전화음질, 16000=음성, 22050 이상=음악)
const OUTPUT_CHANNELS = 1; // 채널 수 (1=모노, 2=스테레오)
const MP3_BITRATE = 64; // mp3 비트레이트(kbps) (음성 48~64, 음악 96~128 권장)

// AudioBuffer 를 목표 샘플레이트/채널로 다운샘플링 (선형 보간)
const resampleBuffer = (source: AudioBuffer, targetRate: number, targetChannels: number): Float32Array[] => {
  const ratio = source.sampleRate / targetRate;
  const outLength = Math.floor((source.length * targetRate) / source.sampleRate);
  const channels: Float32Array[] = [];

  for (let ch = 0; ch < targetChannels; ch += 1) {
    // 소스 채널이 부족하면(모노 소스 등) 0번(좌) 채널을 사용해 원본 레벨 유지
    const inputData = source.getChannelData(Math.min(ch, source.numberOfChannels - 1));
    const outputData = new Float32Array(outLength);

    for (let i = 0; i < outLength; i += 1) {
      const sourceIndex = i * ratio;
      const index = Math.floor(sourceIndex);
      const fraction = sourceIndex - index;
      if (index + 1 < inputData.length) {
        outputData[i] = inputData[index] * (1 - fraction) + inputData[index + 1] * fraction;
      } else {
        outputData[i] = inputData[index];
      }
    }
    channels.push(outputData);
  }
  return channels;
};

// Float32(-1~1) -> Int16 PCM 변환
const floatToInt16 = (input: Float32Array): Int16Array => {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
};

// lamejs 로 mp3 인코딩
const encodeMp3 = (channelData: Float32Array[], sampleRate: number, bitrate: number): Blob => {
  const channels = channelData.length;
  const encoder = new lamejs.Mp3Encoder(channels, sampleRate, bitrate);
  const mp3Data: Uint8Array[] = [];
  const sampleBlockSize = 1152; // MPEG 프레임 크기

  const left = floatToInt16(channelData[0]);
  const right = channels > 1 ? floatToInt16(channelData[1]) : null;

  for (let i = 0; i < left.length; i += sampleBlockSize) {
    const leftChunk = left.subarray(i, i + sampleBlockSize);
    let mp3buf: Uint8Array;
    if (right) {
      const rightChunk = right.subarray(i, i + sampleBlockSize);
      mp3buf = encoder.encodeBuffer(leftChunk, rightChunk);
    } else {
      mp3buf = encoder.encodeBuffer(leftChunk);
    }
    if (mp3buf.length > 0) mp3Data.push(mp3buf);
  }

  const end = encoder.flush();
  if (end.length > 0) mp3Data.push(end);

  // 청크들을 일반 ArrayBuffer 기반 단일 버퍼로 병합 (Blob 타입 호환)
  const totalLength = mp3Data.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;
  mp3Data.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });

  return new Blob([merged], { type: 'audio/mp3' });
};

// mp3 Blob 다운로드 (.mp3 확장자 보장)
const downloadBlob = (blob: Blob, fileName?: string) => {
  const name = fileName || 'audio';
  const downloadName = name.toLowerCase().endsWith('.mp3') ? name : `${name}.mp3`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = downloadName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // 일부 브라우저에서 클릭 직후 즉시 revoke 하면 다운로드가 취소되므로 지연 정리
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 1000);
};

const audioConcat = async (files: CrunkerInputTypes[], fileName?: string) => {
  const crunker = new Crunker();

  try {
    const buffers = await crunker.fetchAudio(...files);
    const concated = crunker.concatAudio(buffers);

    // 목표 포맷으로 다운샘플링 후 mp3 인코딩
    const channelData = resampleBuffer(concated, OUTPUT_SAMPLE_RATE, OUTPUT_CHANNELS);
    const mp3Blob = encodeMp3(channelData, OUTPUT_SAMPLE_RATE, MP3_BITRATE);

    downloadBlob(mp3Blob, fileName);
  } finally {
    // AudioContext 누수 방지 (반복 사용 시 컨텍스트 개수 초과로 실패하는 것을 예방)
    crunker.close();
  }
};

export default audioConcat;
